import { useRef, useState, type ReactNode } from 'react';
import { filesFromDataTransfer } from '../features/ingest/dropFiles';

interface Props {
  onFiles: (files: File[]) => void;
  children?: ReactNode;
  className?: string;
  /** Compact single-line variant for toolbars. */
  compact?: boolean;
}

const ACCEPT = 'image/*,.tif,.tiff,.heic,.heif';

/** Accepts dropped files and folders, or opens a file/folder picker. */
export function DropZone({ onFiles, children, className = '', compact = false }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const pick = (input: HTMLInputElement | null) => input?.click();
  const fromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (files.length) onFiles(files);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setOver(false);
        const files = await filesFromDataTransfer(e.dataTransfer);
        if (files.length) onFiles(files);
      }}
      className={`rounded-xl border-2 border-dashed transition-colors ${over ? 'border-accent bg-accent-soft' : 'border-line'} ${className}`}
    >
      <input ref={fileInput} type="file" multiple accept={ACCEPT} className="hidden" onChange={fromInput} />
      <input
        ref={folderInput}
        type="file"
        className="hidden"
        onChange={fromInput}
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
      />
      {compact ? (
        <div className="flex items-center justify-center gap-2 p-3 text-sm text-ink-2">
          <span>Drop photos or</span>
          <button type="button" className="font-medium text-accent underline" onClick={() => pick(fileInput.current)}>browse</button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
          {children}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => pick(fileInput.current)}
              className="rounded-lg bg-accent px-5 py-2.5 font-medium text-accent-ink hover:opacity-90"
            >
              Choose photos
            </button>
            <button
              type="button"
              onClick={() => pick(folderInput.current)}
              className="rounded-lg border border-line bg-card px-5 py-2.5 font-medium text-ink hover:bg-paper-2"
            >
              Choose a folder
            </button>
          </div>
          <p className="text-xs text-ink-3">JPEG, PNG, WebP, TIFF (incl. 16-bit scans), BMP. You can also paste an image.</p>
        </div>
      )}
    </div>
  );
}
